// ============================================================================
// LE FORMULAIRE DE MESSAGERIE SORTANTE — partagé par la Console et le back-office.
//
// ⚠️ POURQUOI IL EST EXTRAIT : deux écrans règlent aujourd'hui une messagerie (celle de la Console,
// celle du back-office) avec EXACTEMENT les mêmes champs, les mêmes transports et le même secret qui
// ne redescend jamais. Recopié, le second aurait divergé au premier transport ajouté — c'est la forme
// de défaut « deux sources de vérité » (VERIFICATION-Systematique, Forme A). Ce qui change d'un écran
// à l'autre — le titre, ce que la messagerie sert, l'avertissement — arrive en propriétés.
//
// ⚠️ IL AFFICHE CE QUI S'APPLIQUE, pas ce qui est enregistré : la pastille « en vigueur » nomme la
// source RÉELLE (cet écran / configuration serveur / aucun envoi). Un formulaire rempli ne prouve rien
// tant que le serveur n'a pas dit lequel il utilise (§23.0).
// ============================================================================
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { alert, Icon, showToast, type IconName } from '@orgazone/ui-web';
import { Pill } from '../ui';
import type { MailSettings } from '../api';
import { useReportIntegration, type EtatIntegration } from './settings-integrations';

/** L'état d'une messagerie, tel que le serveur le rend — jamais déduit d'un champ rempli. */
export function etatMessagerie(m: MailSettings | null): EtatIntegration {
  if (!m) return { k: 'absent' };
  // `effective` est la source RÉELLEMENT appliquée, pas ce qui est enregistré ici.
  if (m.effective === 'aucun') return { k: 'absent' };
  if (m.effective === 'env') return { k: 'incomplet', manque: 'un réglage complet à l’écran — la configuration serveur prend le relais' };
  return { k: 'configure' };
}

/** Le nom du transport, tel que la carte le montre. */
const TRANSPORT_LABEL: Record<MailSettings['transport'], string> = { smtp: 'SMTP', mailjet: 'Mailjet', none: 'aucun transport' };

/** Ce qu'on soumet — `secret` absent = inchangé (le secret ne redescend jamais, on ne peut que le poser). */
export type MailFormInput = Partial<MailSettings> & { secret?: string };

/** Ce qui distingue un écran de l'autre : les mots, jamais la mécanique. */
export interface MailFormTexts {
  title: string;
  /** À quoi sert cette messagerie — l'opérateur doit savoir ce qu'il coupe en la réglant mal. */
  intro: ReactNode;
  /** L'avertissement du bas : le périmètre exact de ce réglage. */
  footnote: ReactNode;
}

export function MailForm({ data, texts, icone, stockage, onSave, onTest }: {
  data: MailSettings;
  texts: MailFormTexts;
  /** Icône du gabarit d'intégration — les quatre cartes doivent avoir la même anatomie. */
  icone?: IconName;
  /** Où vit ce réglage. ⚠️ Une base magasin appartient au commerçant : nos comptes n'y entrent jamais. */
  stockage?: string;
  onSave: (v: MailFormInput) => Promise<MailSettings>;
  onTest: (to: string) => Promise<{ ok: boolean; error?: string }>;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  useReportIntegration({
    key: texts.title, name: texts.title, provider: TRANSPORT_LABEL[data.transport ?? 'none'],
    etat: etatMessagerie(data), role: texts.intro, storage: stockage ?? '',
  });
  const [f, setF] = useState<MailFormInput>({ ...data, secret: '' });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testRes, setTestRes] = useState<{ ok: boolean; error?: string } | null>(null);

  // Le parent recharge (autre écran, autre source) : le formulaire repart de ce que le serveur dit.
  useEffect(() => { setF({ ...data, secret: '' }); }, [data]);

  const save = async () => {
    setBusy(true); setTestRes(null);
    try {
      const r = await onSave({
        transport: f.transport ?? 'none', fromEmail: f.fromEmail ?? null, fromName: f.fromName ?? null,
        smtpHost: f.smtpHost ?? null, smtpPort: f.smtpPort ?? null, smtpSecure: f.smtpSecure !== false,
        smtpUser: f.smtpUser ?? null, apiKey: f.apiKey ?? null,
        ...(f.secret ? { secret: f.secret } : {}),
      });
      setF({ ...r, secret: '' });
      showToast('Messagerie enregistrée.');
    } catch (e) { void alert({ title: 'Réglage refusé', message: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setTestRes(null);
    try { setTestRes(await onTest(testTo.trim())); }
    catch (e) { setTestRes({ ok: false, error: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const lbl: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 };
  const cell: CSSProperties = { minWidth: 0 };
  const SOURCE = {
    base: { tone: 'ok', txt: 'réglage de cet écran' },
    env: { tone: 'wa', txt: 'configuration serveur (.env)' },
    aucun: { tone: 'da', txt: 'aucun envoi — les e-mails sont seulement journalisés' },
  }[data.effective];

  return (
    <div className="dcard" style={{ overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '14px 18px', background: 'var(--head)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {icone && <Icon name={icone} size={18} />}
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{texts.title}</div>
          {/* ⚠️ LE FOURNISSEUR SE NOMME, comme sur les cartes SMS et portefeuille : « quel service
              envoie mes e-mails ? » est la première question qu'on pose à cette carte, et deux cartes
              sur quatre y répondaient. */}
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
            {TRANSPORT_LABEL[data.transport ?? 'none']}
          </span>
          {/* Ce qui S'APPLIQUE, avec sa source — jamais ce qui est simplement enregistré. C'est la
              seule pastille de cette carte : « en vigueur » est plus fort que « configuré », puisque
              le serveur a déjà tranché quelle source il utilise. */}
          <Pill tone={SOURCE.tone} shape="tight">en vigueur : {SOURCE.txt}</Pill>
        </div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 640, lineHeight: 1.5 }}>
          {texts.intro}
        </div>
        {data.note && <div style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', marginTop: 6, lineHeight: 1.5 }}>⚠️ {data.note}</div>}
      </div>

      <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: '13px 16px' }}>
        <div style={cell}>
          <div style={lbl}>Transport</div>
          <div className="seg-track">
            {([['smtp', 'SMTP'], ['mailjet', 'Mailjet'], ['none', 'Aucun']] as const).map(([k, l]) => (
              <button key={k} type="button" className={f.transport === k ? 'seg active' : 'seg'} onClick={() => setF({ ...f, transport: k })}>{l}</button>
            ))}
          </div>
        </div>
        <div style={cell}>
          <div id={`${fieldId}-2`} style={lbl}>Adresse d'expéditeur</div>
          <input aria-labelledby={`${fieldId}-2`} className="field-input" value={f.fromEmail ?? ''} onChange={(e) => setF({ ...f, fromEmail: e.target.value })} placeholder="no-reply@orgazone.fr" />
        </div>
        <div style={cell}>
          <div id={`${fieldId}-3`} style={lbl}>Nom affiché</div>
          <input aria-labelledby={`${fieldId}-3`} className="field-input" value={f.fromName ?? ''} onChange={(e) => setF({ ...f, fromName: e.target.value })} placeholder="Orgazone" />
        </div>

        {f.transport === 'smtp' && (
          <>
            <div style={cell}>
              <div id={`${fieldId}-4`} style={lbl}>Serveur SMTP</div>
              <input aria-labelledby={`${fieldId}-4`} className="field-input mono" value={f.smtpHost ?? ''} onChange={(e) => setF({ ...f, smtpHost: e.target.value })} placeholder="ssl0.ovh.net" />
            </div>
            <div style={cell}>
              <div id={`${fieldId}-5`} style={lbl}>Port</div>
              <input aria-labelledby={`${fieldId}-5`} className="field-input mono" value={f.smtpPort ?? ''} inputMode="numeric"
                onChange={(e) => setF({ ...f, smtpPort: Number(e.target.value.replace(/\D/g, '')) || null })} placeholder="465" />
            </div>
            <div style={cell}>
              <div style={lbl}>Chiffrement</div>
              <div className="seg-track">
                {([[true, 'SSL/TLS'], [false, 'STARTTLS']] as const).map(([k, l]) => (
                  <button key={String(k)} type="button" className={(f.smtpSecure !== false) === k ? 'seg active' : 'seg'} onClick={() => setF({ ...f, smtpSecure: k })}>{l}</button>
                ))}
              </div>
            </div>
            <div style={cell}>
              <div id={`${fieldId}-0`} style={lbl}>Identifiant</div>
              <input aria-labelledby={`${fieldId}-0`} className="field-input mono" value={f.smtpUser ?? ''} onChange={(e) => setF({ ...f, smtpUser: e.target.value })} />
            </div>
          </>
        )}
        {f.transport === 'mailjet' && (
          <div style={cell}>
            <div id={`${fieldId}-1`} style={lbl}>Clé API</div>
            <input aria-labelledby={`${fieldId}-1`} className="field-input mono" value={f.apiKey ?? ''} onChange={(e) => setF({ ...f, apiKey: e.target.value })} />
          </div>
        )}

        {f.transport !== 'none' && (
          <div style={cell}>
            <div id={`${fieldId}-6`} style={lbl}>{f.transport === 'mailjet' ? 'Secret API' : 'Mot de passe'}</div>
            <input aria-labelledby={`${fieldId}-6`} className="field-input mono" type="password" value={f.secret ?? ''} autoComplete="new-password"
              onChange={(e) => setF({ ...f, secret: e.target.value })}
              placeholder={data.secretSetAt ? '•••••••• (inchangé)' : 'à renseigner'} />
            {/* Le secret ne redescend jamais : on ne peut donc que le REMPLACER, jamais le relire. */}
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.45 }}>
              {data.secretSetAt ? `Défini le ${new Date(data.secretSetAt).toLocaleDateString('fr-FR')} — laisser vide pour le conserver.` : 'Jamais défini.'}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ink200)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        <div style={{ width: 1, height: 24, background: 'var(--ink200)' }} />
        {/* ⚠️ L'ESSENTIEL de cette carte : on SAIT que ça marche au lieu de le supposer. */}
        <input className="field-input" style={{ width: 220 }} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Envoyer un test à…" />
        <button className="btn-ghost" style={{ height: 38, padding: '0 14px', borderRadius: 'var(--r-sm)', fontWeight: 600 }}
          disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(testTo.trim())} onClick={() => void test()}>Envoyer un test</button>
        {testRes && (
          <span style={{ fontSize: 'var(--t-note)', color: testRes.ok ? 'var(--ok-fg)' : 'var(--da-fg)', lineHeight: 1.45, minWidth: 0 }}>
            {testRes.ok ? '✓ Message parti.' : `✕ ${testRes.error ?? 'échec'}`}
          </span>
        )}
      </div>

      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--ink200)', fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.5 }}>
        {texts.footnote}
      </div>

      {/* ⚠️ MÊME PIED QUE LES AUTRES CARTES : « où vit ce réglage » est une question qui se pose sur
          les quatre, et une seule réponse manquante rouvrirait le doute sur toutes. */}
      {stockage && (
        <div style={{
          padding: '9px 18px', borderTop: '1px solid var(--ink200)',
          fontSize: 'var(--t-meta)', color: 'var(--ink500)', display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <Icon name="lock" size={12} />
          {stockage}
        </div>
      )}
    </div>
  );
}
